import { Link } from "react-router-dom";
import { Shield, Lock, Eye, Clock, FileText, Users, AlertTriangle } from "lucide-react";

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/thrively-logo.png" alt="Thrively" className="h-12 w-auto" />
          </Link>
          <Link to="/" className="text-sm text-primary hover:underline font-medium">
            Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="flex items-center gap-3 mb-2">
          <Shield className="h-8 w-8 text-primary" />
          <h1 className="text-3xl font-heading font-bold text-foreground">Privacy Policy</h1>
        </div>
        <p className="text-muted-foreground mb-2">HIPAA-Compliant Health Information Privacy Practices</p>
        <p className="text-sm text-muted-foreground mb-10">Last updated: April 28, 2026</p>

        {/* Notice */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-10 flex gap-3" data-testid="privacy-notice-banner">
          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-amber-900 text-sm">Beta Notice</p>
            <p className="text-sm text-amber-800 mt-1">
              Thrively is currently in beta. While we implement security measures aligned with HIPAA requirements, 
              we advise against entering sensitive Protected Health Information (PHI) until the platform reaches full production status.
            </p>
          </div>
        </div>

        {/* Sections */}
        <div className="space-y-10">
          <Section
            icon={<FileText className="h-5 w-5 text-primary" />}
            title="1. Information We Collect"
          >
            <p>Thrively collects and processes the following categories of information to provide therapy management services:</p>
            <ul className="list-disc pl-5 space-y-2 mt-3">
              <li><strong>Account Information:</strong> Name, email address, and authentication credentials.</li>
              <li><strong>Child Profiles (PHI):</strong> Child's name, date of birth, neurodevelopmental conditions, and therapy-related notes.</li>
              <li><strong>Provider Information:</strong> Therapy provider names, specialties, and contact details.</li>
              <li><strong>Appointment Records:</strong> Scheduling data, session dates, times, and duration.</li>
              <li><strong>Session Notes (PHI):</strong> Clinical observations, mood assessments, and progress ratings.</li>
              <li><strong>Goals & Milestones:</strong> Therapy goals, progress tracking data, and milestone achievements.</li>
              <li><strong>Documents:</strong> Uploaded files including evaluations, IEPs, and medical reports.</li>
              <li><strong>Usage Data:</strong> IP addresses, browser type, and access timestamps for audit purposes.</li>
            </ul>
          </Section>

          <Section
            icon={<Lock className="h-5 w-5 text-primary" />}
            title="2. How We Protect Your Information"
          >
            <p>We implement the following security measures in alignment with HIPAA Security Rule requirements:</p>
            <ul className="list-disc pl-5 space-y-2 mt-3">
              <li><strong>Encryption in Transit:</strong> All data transmitted between your device and our servers is encrypted using TLS 1.2+.</li>
              <li><strong>Access Controls:</strong> Role-based access ensures only authenticated users can view their own data. Each user's PHI is isolated from other users.</li>
              <li><strong>Session Management:</strong> Sessions automatically expire after 1 hour of inactivity, with a 5-minute warning before timeout.</li>
              <li><strong>Token Refresh:</strong> Active users can extend their session securely without re-entering credentials.</li>
              <li><strong>Secure Authentication:</strong> Passwords are hashed using bcrypt with salt rounds. JWT tokens are signed with secure algorithms.</li>
              <li><strong>Database Security:</strong> Data is stored in MongoDB Atlas with network-level access controls and encrypted connections.</li>
            </ul>
          </Section>

          <Section
            icon={<Eye className="h-5 w-5 text-primary" />}
            title="3. Audit Logging & Monitoring"
          >
            <p>In compliance with HIPAA requirements for accountability, Thrively maintains comprehensive audit logs:</p>
            <ul className="list-disc pl-5 space-y-2 mt-3">
              <li><strong>What is Logged:</strong> Every access, creation, modification, or deletion of Protected Health Information (PHI).</li>
              <li><strong>Information Recorded:</strong> User identity, action performed, resource type, resource identifier, IP address, user agent, HTTP status code, and precise timestamp.</li>
              <li><strong>PHI Resources Tracked:</strong> Children profiles, providers, appointments, session notes, goals, activities, and documents.</li>
              <li><strong>Access to Logs:</strong> Users can view their own audit trail via the Audit Log page in their dashboard.</li>
              <li><strong>Retention:</strong> Audit logs are retained for a minimum of 6 years as required by HIPAA.</li>
            </ul>
          </Section>

          <Section
            icon={<Clock className="h-5 w-5 text-primary" />}
            title="4. Session Timeout Policy"
          >
            <p>To protect against unauthorized access to unattended sessions:</p>
            <ul className="list-disc pl-5 space-y-2 mt-3">
              <li>Sessions automatically expire after <strong>60 minutes</strong> of inactivity.</li>
              <li>A warning is displayed <strong>5 minutes</strong> before session expiration.</li>
              <li>Users may choose to extend their session or sign out immediately.</li>
              <li>After expiration, all cached data is cleared and the user must re-authenticate.</li>
              <li>Failed authentication attempts are logged for security monitoring.</li>
            </ul>
          </Section>

          <Section
            icon={<Users className="h-5 w-5 text-primary" />}
            title="5. Your Rights Under HIPAA"
          >
            <p>As a user of Thrively, you have the following rights regarding your health information:</p>
            <ul className="list-disc pl-5 space-y-2 mt-3">
              <li><strong>Right to Access:</strong> You may view and download all PHI stored in your account at any time.</li>
              <li><strong>Right to Correction:</strong> You may request amendments to any inaccurate information in your records.</li>
              <li><strong>Right to Deletion:</strong> You may request deletion of your account and all associated data.</li>
              <li><strong>Right to an Accounting of Disclosures:</strong> You may review the audit log showing all access to your PHI.</li>
              <li><strong>Right to Restrict:</strong> You may request restrictions on how your PHI is used or disclosed.</li>
              <li><strong>Right to Breach Notification:</strong> You will be notified within 60 days of discovery of any breach affecting your PHI.</li>
            </ul>
          </Section>

          <Section
            icon={<Shield className="h-5 w-5 text-primary" />}
            title="6. Data Sharing & Disclosure"
          >
            <p>Thrively does not sell, rent, or share your PHI with third parties except in the following circumstances:</p>
            <ul className="list-disc pl-5 space-y-2 mt-3">
              <li><strong>With Your Consent:</strong> When you explicitly authorize sharing (e.g., exporting to Google Drive or Dropbox).</li>
              <li><strong>Service Providers:</strong> Trusted infrastructure providers (database hosting, cloud storage) who are bound by Business Associate Agreements (BAAs).</li>
              <li><strong>Legal Requirements:</strong> When required by law, court order, or government regulation.</li>
              <li><strong>Emergency:</strong> To prevent a serious threat to health or safety.</li>
            </ul>
            <p className="mt-3">Community posts are shared publicly within the platform. <strong>Do not include PHI in community posts.</strong></p>
          </Section>

          <Section
            icon={<FileText className="h-5 w-5 text-primary" />}
            title="7. Data Retention"
          >
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>Active Accounts:</strong> Data is retained for the duration of your account.</li>
              <li><strong>Deleted Accounts:</strong> Upon account deletion request, all PHI is permanently removed within 30 days.</li>
              <li><strong>Audit Logs:</strong> Retained for a minimum of 6 years per HIPAA requirements.</li>
              <li><strong>Backups:</strong> Encrypted backups are retained for 90 days and then securely destroyed.</li>
            </ul>
          </Section>

          <Section
            icon={<Lock className="h-5 w-5 text-primary" />}
            title="8. Children's Privacy"
          >
            <p>
              Thrively is designed for use by parents and caregivers managing therapy for their children. 
              We do not knowingly collect information directly from children under 13. All child-related 
              data is entered and managed by the authenticated parent or caregiver account holder.
            </p>
          </Section>

          <Section
            icon={<FileText className="h-5 w-5 text-primary" />}
            title="9. Changes to This Policy"
          >
            <p>
              We may update this privacy policy from time to time. Material changes will be communicated 
              via email notification and an in-app banner. Continued use of Thrively after changes constitutes 
              acceptance of the updated policy.
            </p>
          </Section>

          <Section
            icon={<Users className="h-5 w-5 text-primary" />}
            title="10. Contact Us"
          >
            <p>For questions about this privacy policy, to exercise your HIPAA rights, or to report a security concern:</p>
            <div className="mt-3 bg-muted/50 rounded-lg p-4 space-y-1 text-sm">
              <p>Email: support@thrively.me</p>
              <p>Website: thrively.me</p>
            </div>
          </Section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t mt-16 py-8">
        <div className="max-w-4xl mx-auto px-6 text-center text-sm text-muted-foreground">
          <p>&copy; {new Date().getFullYear()} Thrively. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

function Section({ icon, title, children }) {
  return (
    <section>
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <h2 className="text-xl font-heading font-bold text-foreground">{title}</h2>
      </div>
      <div className="text-sm text-muted-foreground leading-relaxed pl-7">
        {children}
      </div>
    </section>
  );
}
